import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import SoftButton from "components/SoftButton";
import { deleteConnectorVersion } from "api/ConnectorVersions";
import { useAuth0 } from "@auth0/auth0-react";
import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import { CircularProgress } from "@mui/material";
import { usePortalFlows } from "hooks/usePortalFlows";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import SoftTypography from "components/SoftTypography";
import SoftBox from "components/SoftBox";
import { ThemedTrashIcon } from "assets/CXFIcons/Triggers";
import { ThemedXIcon } from "assets/CXFIcons/Static";

export const DeleteVersionDialog = ({
  openDeleteVersionDialog,
  onClose,
  versionToDelete,
  onConfirmDelete,
  connector
}) => {
  const { user } = useAuth0();
  const [isDeletingVersion, setIsDeletingVersion] = useState(false);
  const { flows, handleFetchFlows } = usePortalFlows();
  const [isVersionBeingUsed, setIsVersionBeingUsed] = useState(false);
  const [usedInFlows, setUsedInFlows] = useState([]);

  const handleDeleteVersion = async () => {
    if (!user.stripeId || isDeletingVersion || isVersionBeingUsed) return;

    setIsDeletingVersion(true);

    const response = await deleteConnectorVersion({
      id: versionToDelete.id,
      tenant_id: user.stripeId,
      componentKey: connector.id,
      version: versionToDelete.version
    });

    if (response?.resultObject) {
      toast.success(response?.message);
    } else {
      toast.error(response?.message);
    }

    setIsDeletingVersion(false);

    onConfirmDelete();
  };

  const handleCheckIfVersionExistInAFlow = () => {
    const flowsWithVersion = flows.reduce((accu, curr) => {
      try {
        const customNodes = JSON.parse(curr.steps)?.nodes?.filter(
          (node) => node?.type === "custom"
        );
        if (!customNodes) return accu;
        if (
          customNodes.some(
            (node) =>
              node.data.componentId === connector.id &&
              node.data.version === versionToDelete?.version
          )
        ) {
          accu.push(curr.name || curr.flowName);
        }
        return accu;
      } catch {
        return accu;
      }
    }, []);

    setIsVersionBeingUsed(flowsWithVersion.length > 0);
    setUsedInFlows(flowsWithVersion);
  };

  useEffect(() => {
    if (openDeleteVersionDialog) handleFetchFlows();
  }, [openDeleteVersionDialog]);

  useEffect(() => {
    if (versionToDelete) {
      handleCheckIfVersionExistInAFlow();
    }
  }, [flows, versionToDelete]);

  return (
    <Dialog open={openDeleteVersionDialog} onClose={onClose} fullWidth={true} maxWidth="sm">
      <DialogTitle
        sx={{
          marginBottom: "20px",
          padding: "32px 24px 0 24px !important",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
        className="white-to-dark"
        data-testid="form-title"
      >
        <SoftBox display="flex" alignItems="center">
          <ThemedTrashIcon sx={{ fontSize: "16px !important", marginRight: "16px" }} />
          Remove version
        </SoftBox>
        <IconButton onClick={onClose}>
          <ThemedXIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          padding: "0 32px 24px"
        }}
      >
        <SoftBox
          className="border-top"
          sx={{
            paddingTop: "20px"
          }}
        >
          {isVersionBeingUsed ? (
            <>
              <SoftBox
                sx={{
                  alignItems: "center",
                  backgroundColor: "#FFEDED",
                  border: "1px solid #E53E3E",
                  display: "flex",
                  borderRadius: "9px",
                  color: "#E53E3E",
                  height: "59px",
                  justifyContent: "center",
                  marginBottom: "20px"
                }}
              >
                <SoftTypography
                  sx={{
                    color: "#E53E3E",
                    fontSize: "14px",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center"
                  }}
                >
                  <Icon sx={{ fontSize: "16px !important", marginRight: "8px" }}>warning</Icon>
                  Warning: This version is actively being used
                </SoftTypography>
              </SoftBox>
              <SoftTypography
                color="text"
                sx={{
                  fontSize: "16px",
                  marginBottom: "8px",
                  fontWeight: 700
                }}
              >
                Flows using this version:
              </SoftTypography>
              <SoftBox
                sx={{
                  maxHeight: "200px",
                  overflowY: "auto",
                  border: "1px solid #E6E6E6",
                  borderRadius: "8px",
                  padding: "8px",
                  marginBottom: "16px"
                }}
              >
                {usedInFlows.map((flowName, idx) => (
                  <SoftTypography
                    key={idx}
                    sx={{
                      fontSize: "14px",
                      marginBottom: "4px"
                    }}
                  >
                    {flowName}
                  </SoftTypography>
                ))}
              </SoftBox>
            </>
          ) : (
            <>
              <SoftTypography
                color="text"
                sx={{
                  fontSize: "16px",
                  marginBottom: "16px"
                }}
              >
                Are you sure you want to delete this version of the connector?
              </SoftTypography>
              <SoftBox
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  marginTop: "24px"
                }}
              >
                <SoftButton color="info" onClick={handleDeleteVersion} disabled={isDeletingVersion}>
                  {isDeletingVersion ? (
                    <CircularProgress
                      size={24}
                      sx={{
                        color: "#fff",
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        marginTop: "-12px",
                        marginLeft: "-12px",
                        zIndex: 1
                      }}
                    />
                  ) : (
                    "Remove"
                  )}
                </SoftButton>
              </SoftBox>
            </>
          )}
        </SoftBox>
      </DialogContent>
    </Dialog>
  );
};
